/**
 * Tomi v2 Command Center — advanced control surface for the elevated stack.
 *
 * Tabs:
 *   0 — Overview     : engine status, account, risk, session, dry-run flags
 *   1 — Autopilot    : launch/stop, instrument selector
 *   2 — Kaze         : 4-gate intelligence: macro reality, edge class, DOM confirmation, feedback loop
 *   3 — Shadow v2    : depth-gate + log-Kelly divergence vs live decisions
 *   4 — Triple Gate  : strategies + walk-forward / Monte Carlo / variance pillars
 *   5 — Regime       : regime payoff stats + cold-start state
 *   6 — Meta-Learner : safe-bounded parameter nudge proposals
 *   7 — Trades       : tomi_trades dual-write tail + journal
 *   8 — Controls     : start / stop / clear lockout / reset daily / shadow reset
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Alert,
  Tabs,
  Tab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  Stop,
  RestartAlt,
  AutoFixHigh,
  Insights,
  Tune,
  Shield,
  History,
  Bolt,
  CheckCircle,
  Warning,
  Cancel,
  Air,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import api from '../services/api';
import kazeService from '../services/kazeService';
import type {
  RealitySnapshot,
  EdgeCandidate,
  TomiBridgeStatus,
  LearningData,
  KazeHealth,
} from '../services/kazeService';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  BarChart,
  Bar,
} from 'recharts';

// ─── Types ──────────────────────────────────────────────────────────────────

interface EngineSnapshot {
  status: string;
  dryRun: boolean;
  accountId?: number;
  accountName?: string;
  phase?: string;
  riskState?: any;
  sessionId?: string;
  startedAt?: string | null;
  rateLimitStatus?: Record<string, { tokens: number; maxTokens: number }>;
  harnessCheck?: any;
}

interface ShadowDecision {
  signalId: string;
  ts: string;
  v2Action: string;
  v2Contracts: number;
  v2Fraction: number;
  v2KellyRaw: number;
  divergence: 'none' | 'flip_abstain' | 'flip_enter' | 'size_delta';
  sizeDeltaContracts: number;
  liveContracts: number;
  rationale: string[];
}

interface ShadowStats {
  total: number;
  divergences: { none: number; flip_abstain: number; flip_enter: number; size_delta: number };
  v2Actions: { enter: number; abstain: number };
  meanSizeDeltaContracts: number;
  cumulativeSizeDelta: number;
  lastDecisions: ShadowDecision[];
}

interface MetaSuggestion {
  parameter: string;
  currentValue: number;
  suggestedValue: number;
  delta: number;
  rationale: string;
  sampleSize: number;
  emittedAt: string;
}

interface StrategyDescriptor {
  id: string;
  name: string;
  classification?: string;
  walkForwardProfitFactor?: number;
  monteCarloP5ProfitFactor?: number;
  outOfSampleReturnVariance?: number;
  [k: string]: any;
}

interface StrategiesResponse {
  strategies: StrategyDescriptor[];
  registered: string[];
  lastSizing?: any;
}

interface PerfSeriesPoint {
  ts: string;
  net: number;
  cumulative: number;
  contractId: string;
  side: number;
  size: number;
}
interface PerfDailyPoint {
  date: string;
  net: number;
  trades: number;
  cumulative: number;
}
interface AccountPerformance {
  accountId: number;
  balance: number | null;
  initialBalance: number | null;
  realized: number;
  unrealized: number;
  cumulative: number;
  totalTrades: number;
  wins: number;
  losses: number;
  winRate: number;
  profitFactor: number | null;
  grossWin: number;
  grossLoss: number;
  avgWin: number;
  avgLoss: number;
  series: PerfSeriesPoint[];
  daily: PerfDailyPoint[];
}

const POLL_MS = 5000;

// ─── Component ──────────────────────────────────────────────────────────────

export default function TomiV2CommandPage() {
  const [tab, setTab] = useState(0);
  const [snapshot, setSnapshot] = useState<EngineSnapshot | null>(null);
  const [shadow, setShadow] = useState<ShadowStats | null>(null);
  const [meta, setMeta] = useState<MetaSuggestion[]>([]);
  const [strategies, setStrategies] = useState<StrategiesResponse | null>(null);
  const [journal, setJournal] = useState<any[]>([]);
  const [autopilot, setAutopilot] = useState<any>(null);
  const [instruments, setInstruments] = useState<any>(null);
  const [performance, setPerformance] = useState<AccountPerformance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, sh, m, st, j, ap, inst, dec] = await Promise.allSettled([
        api.get('/tomi-engine/status'),
        api.get('/tomi-engine/v2/shadow/stats'),
        api.get('/tomi-engine/v2/meta/suggestions'),
        api.get('/tomi-engine/strategies').catch(() => ({ data: { strategies: [], registered: [] } })),
        api.get('/tomi-engine/journal'),
        api.get('/tomi-engine/status').catch(() => ({ data: null })),
        api.get('/topstep/instruments').catch(() => ({ data: null })),
        api.get('/tomi-engine/decisions?limit=50').catch(() => ({ data: { decisions: [] } })),
      ]);
      if (s.status === 'fulfilled') setSnapshot(s.value.data ?? null);
      if (sh.status === 'fulfilled') setShadow(sh.value.data?.stats ?? null);
      if (m.status === 'fulfilled') setMeta(m.value.data?.suggestions ?? []);
      if (st.status === 'fulfilled') setStrategies(st.value.data ?? null);
      if (j.status === 'fulfilled') setJournal(j.value.data?.trades ?? []);
      if (ap.status === 'fulfilled') {
        const v2 = ap.value.data ?? {};
        const journalArr = (j.status === 'fulfilled' ? j.value.data?.trades : []) ?? [];
        const decData = dec.status === 'fulfilled' ? dec.value.data : { decisions: [], lastScan: null };
        setAutopilot({
          running: v2.status === 'RUNNING',
          status: v2.status,
          session: {
            sessionId: v2.sessionId,
            accountId: v2.accountId,
            instruments: v2.scope?.instruments ?? [],
            instrumentList: v2.scope?.instruments ?? [],
            cycles: v2.cycleStats?.signalsEvaluated ?? v2.harnessCycles ?? journalArr.length,
            cyclesRun: v2.cycleStats?.signalsEvaluated ?? v2.harnessCycles ?? journalArr.length,
            tradesPlaced: v2.cycleStats?.signalsExecuted ?? journalArr.length,
            stats: { tradesExecuted: v2.cycleStats?.signalsExecuted ?? journalArr.length },
            phase: v2.phase,
            startedAt: v2.startedAt,
            decisions: decData.decisions ?? [],
            lastScan: decData.lastScan,
          },
        });
      }
      if (inst.status === 'fulfilled') setInstruments(inst.value.data ?? null);

      // Performance: pull from configured account (snapshot.accountId or autopilot)
      // Fallback: query /topstep/accounts and use first canTrade account
      let acctId: number | undefined =
        (s.status === 'fulfilled' && s.value.data?.accountId) ||
        (ap.status === 'fulfilled' && (
          ap.value.data?.data?.session?.accountId ??
          ap.value.data?.session?.accountId ??
          ap.value.data?.data?.accountId ??
          ap.value.data?.accountId
        ));
      if (!acctId) {
        try {
          const accR = await api.get('/topstep/accounts');
          const list = accR.data?.data ?? accR.data?.accounts ?? [];
          const arr = Array.isArray(list) ? list : [];
          const first = arr.find((a: any) => a?.canTrade) ?? arr[0];
          const id = first?.accountId ?? first?.id;
          if (id) acctId = Number(id);
        } catch { /* ignore */ }
      }
      if (acctId) {
        try {
          const perfR = await api.get(`/topstep/accounts/${acctId}/performance?days=30`);
          if (perfR.data?.success) setPerformance(perfR.data.data);
        } catch { /* keep prior */ }
      }
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const action = async (path: string, label: string, confirm = false) => {
    if (confirm && !window.confirm(`${label}?`)) return;
    try {
      const r = await api.post(`/tomi-engine${path}`);
      setActionMsg(`✅ ${label}: ${r.data?.message ?? 'ok'}`);
      load();
    } catch (e) {
      setActionMsg(`❌ ${label}: ${e instanceof Error ? e.message : 'failed'}`);
    }
    setTimeout(() => setActionMsg(null), 6000);
  };

  // ── Status badge ────────────────────────────────────────────────────────
  const statusColor = (st?: string): 'success' | 'warning' | 'error' | 'default' => {
    if (st === 'RUNNING') return 'success';
    if (st === 'INITIALIZING' || st === 'STARTING') return 'warning';
    if (st === 'ERROR' || st === 'HALTED') return 'error';
    return 'default';
  };

  return (
    <Box sx={{ p: 3, color: '#e6e6e6' }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff' }}>
            <Bolt sx={{ verticalAlign: 'middle', color: '#facc15', mr: 1 }} />
            Tomi v2 Command Center
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            Elevated Stack — depth gate · log-Kelly · triple-consensus · regime veto · meta-learner
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={`Status: ${snapshot?.status ?? 'IDLE'}`}
            color={statusColor(snapshot?.status)}
            size="small"
          />
          <Chip
            label={snapshot?.dryRun ? 'DRY-RUN' : 'LIVE'}
            color={snapshot?.dryRun ? 'warning' : 'success'}
            size="small"
          />
          {snapshot?.accountId && (
            <Chip label={`Acct ${snapshot.accountId}`} size="small" variant="outlined" />
          )}
          <IconButton onClick={load} sx={{ color: '#9ca3af' }}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {actionMsg && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {actionMsg}
        </Alert>
      )}

      {loading && !snapshot ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: '1px solid #374151',
              mb: 2,
              '& .MuiTab-root': { color: '#9ca3af' },
              '& .Mui-selected': { color: '#facc15 !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#facc15' },
            }}
          >
            <Tab label="Overview" icon={<Insights />} iconPosition="start" />
            <Tab label="Autopilot" icon={<PlayArrow />} iconPosition="start" />
            <Tab label="Kaze" icon={<Air />} iconPosition="start" />
            <Tab label="Shadow v2" icon={<AutoFixHigh />} iconPosition="start" />
            <Tab label="Triple Gate" icon={<Shield />} iconPosition="start" />
            <Tab label="Regime" icon={<Tune />} iconPosition="start" />
            <Tab label="Meta-Learner" icon={<AutoFixHigh />} iconPosition="start" />
            <Tab label="Trades" icon={<History />} iconPosition="start" />
            <Tab label="Controls" icon={<RestartAlt />} iconPosition="start" />
          </Tabs>

          {tab === 0 && <OverviewTab snapshot={snapshot} shadow={shadow} strategies={strategies} autopilot={autopilot} performance={performance} />}
          {tab === 1 && <AutopilotTab autopilot={autopilot} instruments={instruments} onAction={action} api={api} reload={load} />}
          {tab === 2 && <KazeTab />}
          {tab === 3 && <ShadowTab shadow={shadow} onReset={() => action('/v2/shadow/reset', 'Shadow reset', true)} />}
          {tab === 4 && <TripleGateTab strategies={strategies} />}
          {tab === 5 && <RegimeTab snapshot={snapshot} />}
          {tab === 6 && <MetaTab meta={meta} />}
          {tab === 7 && <TradesTab journal={journal} />}
          {tab === 8 && <ControlsTab snapshot={snapshot} onAction={action} />}
        </>
      )}
    </Box>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

function OverviewTab({
  snapshot,
  shadow,
  strategies,
  autopilot,
  performance,
}: {
  snapshot: EngineSnapshot | null;
  shadow: ShadowStats | null;
  strategies: StrategiesResponse | null;
  autopilot: any;
  performance: AccountPerformance | null;
}) {
  const total = shadow?.total ?? 0;
  const agreePct = total > 0 ? ((shadow!.divergences.none / total) * 100).toFixed(1) : '0.0';
  const apRunning = autopilot?.running ?? autopilot?.data?.running ?? autopilot?.isRunning ?? autopilot?.data?.isRunning;

  const realized = performance?.realized ?? 0;
  const unrealized = performance?.unrealized ?? 0;
  const cumulative = performance?.cumulative ?? 0;
  const balance = performance?.balance;
  const initialBalance = performance?.initialBalance;
  const winRate = performance?.winRate ?? 0;
  const pf = performance?.profitFactor;

  const fmtUsd = (n: number) =>
    `${n >= 0 ? '+' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pnlColor = (n: number) => (n > 0 ? '#10b981' : n < 0 ? '#ef4444' : '#9ca3af');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <KpiCard label="Engine Status" value={snapshot?.status ?? 'IDLE'} accent={snapshot?.status === 'RUNNING' ? '#10b981' : '#9ca3af'} />
      </Grid>
      <Grid item xs={12} md={3}>
        <KpiCard label="Autopilot" value={apRunning ? 'RUNNING' : 'STOPPED'} accent={apRunning ? '#10b981' : '#6b7280'} />
      </Grid>
      <Grid item xs={12} md={3}>
        <KpiCard label="Mode" value={snapshot?.dryRun ? 'DRY-RUN' : 'LIVE'} accent={snapshot?.dryRun ? '#f59e0b' : '#10b981'} />
      </Grid>
      <Grid item xs={12} md={3}>
        <KpiCard label="Account" value={snapshot?.accountName ?? '—'} sub={snapshot?.accountId ? `#${snapshot.accountId}` : undefined} />
      </Grid>

      {/* ── PnL Row ────────────────────────────────────────────────────────── */}
      <Grid item xs={12} md={3}>
        <KpiCard
          label="Account Balance"
          value={balance !== null && balance !== undefined ? `$${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
          sub={initialBalance !== null && initialBalance !== undefined ? `start $${initialBalance.toLocaleString()}` : undefined}
          accent="#facc15"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <KpiCard
          label="Realized PnL (30d)"
          value={fmtUsd(realized)}
          sub={performance ? `${performance.totalTrades} trades` : 'no data'}
          accent={pnlColor(realized)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <KpiCard
          label="Unrealized PnL"
          value={fmtUsd(unrealized)}
          sub={unrealized !== 0 ? 'open positions' : 'flat'}
          accent={pnlColor(unrealized)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <KpiCard
          label="Cumulative PnL"
          value={fmtUsd(cumulative)}
          sub={`WR ${(winRate * 100).toFixed(1)}% · PF ${pf !== null && pf !== undefined ? pf.toFixed(2) : '∞'}`}
          accent={pnlColor(cumulative)}
        />
      </Grid>

      {/* ── Performance Chart ────────────────────────────────────────────── */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #1e293b', height: 360 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" sx={{ color: '#facc15', fontWeight: 700, letterSpacing: 0.5 }}>
              📈 Cumulative PnL · Last 30 Days
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {performance ? `${performance.series.length} fills` : 'loading…'}
            </Typography>
          </Box>
          {performance && performance.series.length > 0 ? (
            <ResponsiveContainer width="100%" height="90%">
              <AreaChart data={performance.series}>
                <defs>
                  <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={cumulative >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={cumulative >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="ts"
                  stroke="#6b7280"
                  fontSize={10}
                  tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  minTickGap={50}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={10}
                  tickFormatter={(v) => `$${v.toFixed(0)}`}
                />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#020617', border: '1px solid #1e293b', borderRadius: 4 }}
                  labelFormatter={(v) => new Date(v as string).toLocaleString()}
                  formatter={(value: any, name: string) => [`$${Number(value).toFixed(2)}`, name]}
                />
                <ReferenceLine y={0} stroke="#374151" strokeDasharray="2 2" />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke={cumulative >= 0 ? '#10b981' : '#ef4444'}
                  strokeWidth={2}
                  fill="url(#pnlGrad)"
                  name="Cumulative"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="90%">
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {performance ? 'No trades in window' : 'Loading performance…'}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* ── Daily PnL Bars ──────────────────────────────────────────────── */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, bgcolor: '#0f172a', border: '1px solid #1e293b', height: 360 }}>
          <Typography variant="subtitle2" sx={{ color: '#facc15', fontWeight: 700, letterSpacing: 0.5, mb: 1 }}>
            📊 Daily Net PnL
          </Typography>
          {performance && performance.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={performance.daily.slice(-15)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  fontSize={10}
                  tickFormatter={(v) => v.slice(5)}
                />
                <YAxis stroke="#6b7280" fontSize={10} tickFormatter={(v) => `$${v.toFixed(0)}`} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#020617', border: '1px solid #1e293b', borderRadius: 4 }}
                  formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Net']}
                />
                <ReferenceLine y={0} stroke="#374151" strokeDasharray="2 2" />
                <Bar dataKey="net" name="Net" fill="#3b82f6">
                  {performance.daily.slice(-15).map((d, i) => (
                    <Bar key={i} dataKey="net" fill={d.net >= 0 ? '#10b981' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="90%">
              <Typography variant="body2" sx={{ color: '#6b7280' }}>No daily data</Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* ── Existing Stats Row ────────────────────────────────────────────── */}
      <Grid item xs={12} md={4}>
        <KpiCard label="Shadow Samples" value={String(total)} sub={`agree ${agreePct}%`} accent="#60a5fa" />
      </Grid>
      <Grid item xs={12} md={4}>
        <KpiCard
          label="Strategies Registered"
          value={String(strategies?.registered?.length ?? 0)}
          sub={`of ${strategies?.strategies?.length ?? 0} declared`}
          accent="#a78bfa"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <KpiCard label="Phase" value={snapshot?.phase ?? '—'} accent="#facc15" />
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937' }}>
          <Typography variant="subtitle2" sx={{ color: '#9ca3af', mb: 1 }}>
            Risk State
          </Typography>
          {snapshot?.riskState ? (
            <Box component="pre" sx={{ fontSize: 12, color: '#d1d5db', m: 0, maxHeight: 240, overflow: 'auto' }}>
              {JSON.stringify(snapshot.riskState, null, 2)}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              No risk state — engine not initialized. Use the <strong>Autopilot</strong> tab to launch a session.
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

// ─── Autopilot Tab ──────────────────────────────────────────────────────────

function AutopilotTab({
  autopilot,
  instruments,
  onAction,
  api,
  reload,
}: {
  autopilot: any;
  instruments: any;
  onAction: (path: string, label: string, confirm?: boolean) => void;
  api: any;
  reload: () => void;
}) {
  const session = autopilot?.session ?? autopilot?.data?.session ?? autopilot?.data ?? autopilot ?? {};
  const isRunning = autopilot?.running ?? autopilot?.data?.running ?? session?.isRunning ?? session?.running;
  const apiInstruments: any[] = instruments?.instruments ?? [];
  const defaults: string[] = instruments?.defaults ?? ['MNQ', 'MES', 'MGC', 'M2K', 'MYM'];
  const [selected, setSelected] = useState<string[]>(defaults);
  const [busy, setBusy] = useState(false);
  const [launchMsg, setLaunchMsg] = useState<string | null>(null);

  useEffect(() => {
    if (defaults.length > 0 && selected.length === 0) setSelected(defaults);
  }, [defaults.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (sym: string) => {
    setSelected((prev) => (prev.includes(sym) ? prev.filter((s) => s !== sym) : [...prev, sym]));
  };

  const launch = async () => {
    if (selected.length === 0) {
      setLaunchMsg('❌ Pick at least one instrument.');
      return;
    }
    if (!window.confirm(`Launch v2 autopilot on ${selected.length} instrument(s): ${selected.join(', ')}?`)) return;
    setBusy(true);
    setLaunchMsg(null);
    try {
      // v2 engine: initialize → scope → start → feed/start
      await api.post('/tomi-engine/initialize', {}).catch(() => null);
      await api.post('/tomi-engine/scope', {
        instruments: selected,
        sessionWindows: [{ start: '00:00', end: '23:59' }],
      });
      const r = await api.post('/tomi-engine/start', {});
      await api.post('/tomi-engine/feed/start', { symbols: selected }).catch(() => null);
      setLaunchMsg(`✅ Autopilot launched: ${r.data?.status ?? 'RUNNING'}`);
      reload();
    } catch (e: any) {
      setLaunchMsg(`❌ Launch failed: ${e?.response?.data?.error ?? e?.message ?? 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (!window.confirm('Stop autopilot?')) return;
    setBusy(true);
    try {
      await api.post('/tomi-engine/feed/stop', {}).catch(() => null);
      await api.post('/tomi-engine/stop', {});
      setLaunchMsg('✅ Autopilot stopped.');
      reload();
    } catch (e: any) {
      setLaunchMsg(`❌ Stop failed: ${e?.response?.data?.error ?? e?.message ?? 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  const grouped = {
    'Micro Equity Index': apiInstruments.filter((i) => ['MNQ', 'MES', 'MYM', 'M2K'].includes(i.symbol)),
    'Micro Metals & Energy': apiInstruments.filter((i) => ['MGC', 'MCL'].includes(i.symbol)),
    'Full-Size (opt-in)': apiInstruments.filter((i) => i.isFullSize),
    'FX & Rates': apiInstruments.filter((i) => i.symbol.startsWith('6') || ['M6E', 'ZT', 'ZN', 'ZB'].includes(i.symbol)),
    'Other': apiInstruments.filter(
      (i) => !i.isFullSize && !['MNQ', 'MES', 'MYM', 'M2K', 'MGC', 'MCL'].includes(i.symbol) && !i.symbol.startsWith('6') && !['M6E', 'ZT', 'ZN', 'ZB'].includes(i.symbol),
    ),
  };

  return (
    <Box>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={3}>
          <KpiCard label="Autopilot" value={isRunning ? 'RUNNING' : 'STOPPED'} accent={isRunning ? '#10b981' : '#6b7280'} />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Active Instruments" value={String(session?.instrumentList?.length ?? session?.instruments?.length ?? selected.length)} sub={(session?.instrumentList ?? session?.instruments ?? selected).join(' · ') || '—'} />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Cycles Run" value={String(session?.cycles ?? session?.cyclesRun ?? 0)} accent="#60a5fa" />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Trades This Session" value={String(session?.stats?.tradesExecuted ?? session?.tradesPlaced ?? 0)} accent="#a78bfa" />
        </Grid>
      </Grid>

      {launchMsg && (
        <Alert severity={launchMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setLaunchMsg(null)}>
          {launchMsg}
        </Alert>
      )}

      <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937', mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff' }}>Instrument Selection</Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {selected.length} selected · defaults: {defaults.join(', ')}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button size="small" variant="outlined" onClick={() => setSelected(defaults)}>Defaults</Button>
            <Button size="small" variant="outlined" onClick={() => setSelected(apiInstruments.map((i) => i.symbol))}>All</Button>
            <Button size="small" variant="outlined" onClick={() => setSelected([])}>None</Button>
          </Box>
        </Box>

        {Object.entries(grouped).map(([group, items]) =>
          items.length === 0 ? null : (
            <Box key={group} mb={2}>
              <Typography variant="caption" sx={{ color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {group}
              </Typography>
              <Grid container spacing={1} mt={0.5}>
                {items.map((inst: any) => {
                  const on = selected.includes(inst.symbol);
                  return (
                    <Grid item key={inst.symbol}>
                      <Chip
                        label={
                          <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ py: 0.5 }}>
                            <span style={{ fontWeight: 600 }}>
                              {inst.symbol}
                              {inst.isFullSize && ' ⚠'}
                            </span>
                            <span style={{ fontSize: 10, opacity: 0.7 }}>{inst.fullName}</span>
                          </Box>
                        }
                        onClick={() => toggle(inst.symbol)}
                        sx={{
                          height: 'auto',
                          py: 0.5,
                          bgcolor: on ? '#065f46' : '#1f2937',
                          color: on ? '#fff' : '#9ca3af',
                          border: on ? '1px solid #10b981' : '1px solid #374151',
                          '&:hover': { bgcolor: on ? '#047857' : '#374151' },
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ),
        )}
      </Paper>

      <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937' }}>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>Launch Controls</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="success"
            size="large"
            disabled={busy || isRunning}
            startIcon={<PlayArrow />}
            onClick={launch}
            sx={{ minWidth: 200 }}
          >
            {isRunning ? 'Already Running' : `Launch Autopilot (${selected.length})`}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="large"
            disabled={busy || !isRunning}
            startIcon={<Stop />}
            onClick={stop}
            sx={{ minWidth: 160 }}
          >
            Stop Autopilot
          </Button>
          <Button
            variant="outlined"
            color="info"
            size="large"
            disabled={busy || !isRunning}
            onClick={() => onAction('../topstep/autopilot/force-cycle', 'Force trade cycle', false)}
          >
            Force Cycle
          </Button>
        </Box>
      </Paper>

      {/* Recent Decisions — live cycle log */}
      <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937', mt: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="h6" sx={{ color: '#fbbf24' }}>
            📜 Recent Decisions
          </Typography>
          <Box display="flex" gap={1.5} alignItems="center">
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {session?.lastScan ? `Last scan: ${new Date(session.lastScan).toLocaleTimeString()}` : 'No scan yet'}
            </Typography>
            {session?.nextScan && (
              <Typography variant="caption" sx={{ color: '#60a5fa' }}>
                Next: {new Date(session.nextScan).toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Box>
        {(() => {
          const decisions = (session?.decisions ?? []) as any[];
          if (decisions.length === 0) {
            return (
              <Typography sx={{ color: '#6b7280', textAlign: 'center', py: 3 }}>
                No decisions yet — engine waiting for first cycle
              </Typography>
            );
          }
          const recent = [...decisions].reverse().slice(0, 30);
          const colorFor = (action: string) => {
            const a = (action ?? '').toUpperCase();
            if (a.includes('EXEC') || a === 'ENTER') return '#10b981';
            if (a.includes('HALT') || a.includes('REJECT') || a.includes('VETO') || a.includes('BROKEN')) return '#ef4444';
            if (a.includes('SKIP') || a.includes('CLOSED')) return '#6b7280';
            if (a.includes('NORMAL') || a === 'ELEVATED') return '#34d399';
            if (a.includes('DEGRADED') || a.includes('WARN')) return '#f59e0b';
            return '#60a5fa';
          };
          return (
            <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
              {recent.map((d, i) => {
                const ts = d.timestamp ? new Date(d.timestamp).toLocaleTimeString() : '';
                const action = d.action ?? '—';
                const sym = d.symbol ?? '';
                const reason = d.reason ?? '';
                const kz = typeof d.kazeScore === 'number' ? d.kazeScore.toFixed(3) : null;
                const tc = typeof d.tomiComposite === 'number' ? d.tomiComposite.toFixed(3) : null;
                return (
                  <Box
                    key={d.id ?? i}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 0.75,
                      px: 1,
                      borderBottom: '1px solid #1f2937',
                      '&:hover': { bgcolor: '#0f172a' },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#6b7280', minWidth: 70, fontFamily: 'monospace' }}>
                      {ts}
                    </Typography>
                    <Chip
                      label={action}
                      size="small"
                      sx={{
                        bgcolor: colorFor(action) + '20',
                        color: colorFor(action),
                        border: `1px solid ${colorFor(action)}40`,
                        fontWeight: 600,
                        minWidth: 110,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#e5e7eb', minWidth: 60, fontWeight: 600 }}>
                      {sym}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af', flex: 1, fontSize: 13 }}>
                      {reason}
                    </Typography>
                    {(kz || tc) && (
                      <Box display="flex" gap={1} sx={{ minWidth: 140 }}>
                        {kz && (
                          <Typography variant="caption" sx={{ color: '#a78bfa', fontFamily: 'monospace' }}>
                            K:{kz}
                          </Typography>
                        )}
                        {tc && (
                          <Typography variant="caption" sx={{ color: '#34d399', fontFamily: 'monospace' }}>
                            T:{tc}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {d.executed && (
                      <Chip label="EXEC" size="small" sx={{ bgcolor: '#10b98120', color: '#10b981', fontWeight: 700 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })()}
      </Paper>
    </Box>
  );
}

// ─── Kaze Tab ───────────────────────────────────────────────────────────────

function kellyMultiplier(edge: EdgeCandidate): { mult: string; color: string } {
  const cs = edge.consensus_state?.toLowerCase() ?? '';
  if (cs.includes('crowded')) return { mult: '0.5×', color: '#f59e0b' };
  const score = edge.kaze_score;
  if (score >= 0.85) return { mult: '1.5×', color: '#10b981' };
  if (score >= 0.70) return { mult: '1.25×', color: '#34d399' };
  if (score >= 0.50) return { mult: '1.0×', color: '#9ca3af' };
  return { mult: 'VETO', color: '#ef4444' };
}

function GateBadge({ ok, label }: { ok: boolean | null; label: string }) {
  const color = ok === null ? '#9ca3af' : ok ? '#10b981' : '#ef4444';
  const icon = ok === null ? '○' : ok ? '✓' : '✗';
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography sx={{ color, fontWeight: 700, fontSize: 14 }}>{icon}</Typography>
      <Typography variant="caption" sx={{ color }}>{label}</Typography>
    </Box>
  );
}

function KazeTab() {
  const [reality, setReality] = useState<RealitySnapshot | null>(null);
  const [edges, setEdges] = useState<EdgeCandidate[]>([]);
  const [bridge, setBridge] = useState<TomiBridgeStatus | null>(null);
  const [learning, setLearning] = useState<LearningData | null>(null);
  const [health, setHealth] = useState<KazeHealth | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [fetching, setFetching] = useState(false);
  const [kazeError, setKazeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const [h, r, e, b, l] = await Promise.allSettled([
        kazeService.getHealth(),
        kazeService.getReality(),
        kazeService.getEdges(),
        kazeService.getTomiBridge(),
        kazeService.getLearning(),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value);
      if (r.status === 'fulfilled') setReality(r.value);
      if (e.status === 'fulfilled') setEdges(e.value ?? []);
      if (b.status === 'fulfilled') setBridge(b.value ?? null);
      if (l.status === 'fulfilled') setLearning(l.value ?? null);
      setLastRefresh(new Date());
      setKazeError(null);
    } catch {
      setKazeError('Kaze service unreachable');
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  // ── Derived gate states ────────────────────────────────────────────────────
  const kazeOnline = !!health && ['ok', 'healthy', 'operational', 'running'].includes(String(health.status).toLowerCase());
  const riskPosture = reality?.risk_posture ?? null;
  const macroRegime = reality?.macro_regime?.state ?? 'UNKNOWN';
  const macroConf = reality?.macro_regime?.confidence ?? 0;
  const liquidity = reality?.liquidity_state ?? 'UNKNOWN';
  const vixLevel = reality?.volatility_state?.vix_level ?? null;
  const gate1ok = kazeOnline && riskPosture !== null && !['CRISIS', 'RISK_OFF'].includes(riskPosture ?? '');
  const gate2ok = edges.length > 0 && edges.some(e => e.kaze_score >= 0.5 && !e.consensus_state?.toLowerCase().includes('crowded'));
  const gate3ok = bridge != null && (bridge.total_forwarded ?? 0) >= 0;
  const gate4ok = learning != null && (learning.total_feedback_records ?? 0) > 0;

  // ── Harness mode derived from gates ───────────────────────────────────────
  const harnessMode = !kazeOnline
    ? 'LOCKOUT'
    : !gate1ok
    ? 'SIMULATION'
    : gate1ok && gate2ok
    ? 'MICRO-LIVE'
    : 'SIMULATION';

  const modeColor: Record<string, string> = {
    'MICRO-LIVE': '#10b981',
    SIMULATION: '#f59e0b',
    LOCKOUT: '#ef4444',
    'CONTROLLED-LIVE': '#3b82f6',
  };

  // ── Trust score derived from bridge + learning ─────────────────────────────
  const hitRate = learning?.edge_hit_rate;
  const hitRateNum = typeof hitRate === 'number' ? hitRate : parseFloat(hitRate as string) || null;
  const trustScore = hitRateNum !== null ? Math.min(1, hitRateNum) : null;

  const postureColor = (p: string | null) => {
    if (!p) return '#9ca3af';
    if (['RISK_ON', 'BULLISH'].includes(p)) return '#10b981';
    if (['NEUTRAL', 'CAUTIOUS'].includes(p)) return '#f59e0b';
    return '#ef4444';
  };

  const regimeTrend = reality?.macro_regime?.trend_direction;
  const TrendIcon = regimeTrend === 'UP' ? TrendingUp : regimeTrend === 'DOWN' ? TrendingDown : TrendingFlat;

  return (
    <Box>
      {/* ── Header row ──────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Air sx={{ color: '#38bdf8' }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
            Kaze no Dōsatsu — Intelligence Gates
          </Typography>
          <Chip
            label={kazeOnline ? 'ONLINE' : 'OFFLINE'}
            size="small"
            sx={{ bgcolor: kazeOnline ? '#052e16' : '#450a0a', color: kazeOnline ? '#10b981' : '#ef4444', fontWeight: 700 }}
          />
          <Chip
            label={`Mode: ${harnessMode}`}
            size="small"
            sx={{ bgcolor: '#111827', color: modeColor[harnessMode] ?? '#9ca3af', fontWeight: 700 }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {lastRefresh && (
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
          <IconButton size="small" onClick={refresh} disabled={fetching} sx={{ color: '#9ca3af' }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {kazeError && <Alert severity="warning" sx={{ mb: 2 }}>{kazeError}</Alert>}

      {/* ── Gate badges row ─────────────────────────────────────────────────── */}
      <Box display="flex" gap={3} mb={2} p={1.5} sx={{ bgcolor: '#0f172a', borderRadius: 1, border: '1px solid #1e293b' }}>
        <GateBadge ok={gate1ok} label="Macro Reality" />
        <GateBadge ok={gate2ok} label="Edge Class + Kelly" />
        <GateBadge ok={gate3ok} label="DOM Confirmation" />
        <GateBadge ok={gate4ok} label="Feedback Loop" />
      </Box>

      <Grid container spacing={2}>
        {/* ── Gate 1: Macro Reality ─────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: '#0f172a', border: `1px solid ${gate1ok ? '#064e3b' : '#1e293b'}`, borderLeft: `3px solid ${gate1ok ? '#10b981' : '#374151'}`, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ color: '#38bdf8', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              Gate 1 · Macro Reality
            </Typography>
            <Grid container spacing={1} mb={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>Regime</Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TrendIcon sx={{ fontSize: 16, color: '#facc15' }} />
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>{macroRegime}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>({(macroConf * 100).toFixed(0)}%)</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>Risk Posture</Typography>
                <Typography sx={{ color: postureColor(riskPosture), fontWeight: 600 }}>{riskPosture ?? 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>Liquidity</Typography>
                <Typography sx={{ color: '#e2e8f0' }}>{liquidity}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>VIX Level</Typography>
                <Typography sx={{ color: vixLevel && vixLevel > 25 ? '#ef4444' : '#e2e8f0' }}>
                  {vixLevel !== null ? vixLevel.toFixed(1) : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ borderColor: '#1e293b', my: 1 }} />
            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Dominant Narratives</Typography>
            {(reality?.dominant_narratives ?? []).slice(0, 3).map((n, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
                <Chip
                  label={`${(n.strength * 100).toFixed(0)}%`}
                  size="small"
                  sx={{ bgcolor: '#1e293b', color: '#facc15', fontSize: 11 }}
                />
                <Typography variant="caption" sx={{ color: n.price_confirmation ? '#10b981' : '#9ca3af' }}>
                  {n.name ?? n.narrative}
                </Typography>
                {n.price_confirmation && <CheckCircle sx={{ fontSize: 12, color: '#10b981' }} />}
              </Box>
            ))}
            {(reality?.dominant_narratives ?? []).length === 0 && (
              <Typography variant="caption" sx={{ color: '#4b5563' }}>No narratives</Typography>
            )}
            {(reality?.catalyst_proximity ?? []).length > 0 && (
              <>
                <Divider sx={{ borderColor: '#1e293b', my: 1 }} />
                <Typography variant="caption" sx={{ color: '#f87171', display: 'block', mb: 0.5 }}>
                  ⚡ Catalysts ({reality!.catalyst_proximity.length})
                </Typography>
                {reality!.catalyst_proximity.slice(0, 2).map((c, i) => (
                  <Typography key={i} variant="caption" sx={{ color: '#fca5a5', display: 'block' }}>
                    {c.event} — {c.severity}
                  </Typography>
                ))}
              </>
            )}
          </Paper>
        </Grid>

        {/* ── Gate 2: Intelligence Edges ───────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: '#0f172a', border: `1px solid ${gate2ok ? '#064e3b' : '#1e293b'}`, borderLeft: `3px solid ${gate2ok ? '#10b981' : '#374151'}`, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ color: '#38bdf8', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              Gate 2 · Intelligence Edges + Kelly Class
            </Typography>
            {edges.length === 0 ? (
              <Typography variant="caption" sx={{ color: '#4b5563' }}>No edges available</Typography>
            ) : (
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {edges.slice(0, 8).map((e) => {
                  const km = kellyMultiplier(e);
                  const isVeto = km.mult === 'VETO';
                  return (
                    <Box key={e.candidate_id} mb={1} p={1} sx={{ bgcolor: '#111827', borderRadius: 1, border: `1px solid ${isVeto ? '#450a0a' : '#1e293b'}` }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Chip label={e.asset} size="small" sx={{ bgcolor: '#1e293b', color: '#facc15', fontSize: 11, height: 20 }} />
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{e.edge_type}</Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Typography variant="caption" sx={{ color: km.color, fontWeight: 700 }}>{km.mult}</Typography>
                          <Chip label={e.consensus_state} size="small" sx={{ bgcolor: '#1e293b', color: '#94a3b8', fontSize: 10, height: 18 }} />
                        </Box>
                      </Box>
                      <Box mt={0.5}>
                        <LinearProgress
                          variant="determinate"
                          value={e.kaze_score * 100}
                          sx={{ height: 4, borderRadius: 2, bgcolor: '#1e293b', '& .MuiLinearProgress-bar': { bgcolor: e.kaze_score >= 0.7 ? '#10b981' : e.kaze_score >= 0.5 ? '#f59e0b' : '#ef4444' } }}
                        />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>{e.narrative_context}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Gate 3: DOM + Intelligence Confirmation ───────────────────────── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: '#0f172a', border: `1px solid ${gate3ok ? '#172554' : '#1e293b'}`, borderLeft: `3px solid ${gate3ok ? '#3b82f6' : '#374151'}`, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ color: '#38bdf8', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              Gate 3 · DOM + Intelligence Confirmation
            </Typography>
            {!bridge ? (
              <Typography variant="caption" sx={{ color: '#4b5563' }}>Bridge not connected</Typography>
            ) : (
              <>
                <Box display="flex" gap={2} mb={1}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Tomi Health</Typography>
                    <Typography sx={{ color: bridge.tomi_health?.status === 'ok' ? '#10b981' : '#f59e0b' }}>
                      {bridge.tomi_health?.status ?? 'unknown'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Signals Forwarded</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>{bridge.total_forwarded}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ borderColor: '#1e293b', my: 1 }} />
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Recent Signals</Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                  {(bridge.signals_forwarded ?? []).slice(-5).reverse().map((s, i) => (
                    <Box key={i} display="flex" justifyContent="space-between" mb={0.5} p={0.5} sx={{ bgcolor: '#111827', borderRadius: 0.5 }}>
                      <Box display="flex" gap={0.5}>
                        <Chip label={s.kaze_class} size="small" sx={{ bgcolor: '#1e293b', color: '#a78bfa', fontSize: 10, height: 18 }} />
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{s.assets?.join(', ')}</Typography>
                      </Box>
                      <Box display="flex" gap={0.5} alignItems="center">
                        <Typography variant="caption" sx={{ color: '#facc15' }}>{s.tomi_action}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>{new Date(s.timestamp).toLocaleTimeString()}</Typography>
                      </Box>
                    </Box>
                  ))}
                  {(bridge.signals_forwarded ?? []).length === 0 && (
                    <Typography variant="caption" sx={{ color: '#4b5563' }}>No signals forwarded yet</Typography>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* ── Gate 4: Feedback Loop ─────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: '#0f172a', border: `1px solid ${gate4ok ? '#064e3b' : '#1e293b'}`, borderLeft: `3px solid ${gate4ok ? '#10b981' : '#374151'}`, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ color: '#38bdf8', mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              Gate 4 · Feedback Loop
            </Typography>
            {!learning ? (
              <Typography variant="caption" sx={{ color: '#4b5563' }}>No learning data yet</Typography>
            ) : (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Total Feedback Records</Typography>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>{learning.total_feedback_records}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Edge Hit Rate</Typography>
                  <Typography sx={{ color: hitRateNum !== null && hitRateNum >= 0.82 ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                    {hitRateNum !== null ? `${(hitRateNum * 100).toFixed(1)}%` : String(learning.edge_hit_rate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Edge Realized</Typography>
                  <Typography sx={{ color: '#e2e8f0' }}>{learning.edge_realized_count}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>False Positives</Typography>
                  <Typography sx={{ color: '#f87171' }}>{learning.false_positive_count}</Typography>
                </Grid>
                {hitRateNum !== null && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Hit Rate Progress</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, hitRateNum * 100)}
                      sx={{ height: 6, borderRadius: 3, bgcolor: '#1e293b', '& .MuiLinearProgress-bar': { bgcolor: hitRateNum >= 0.82 ? '#10b981' : '#f59e0b' } }}
                    />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>0%</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>Gate ≥82%</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>100%</Typography>
                    </Box>
                  </Grid>
                )}
                {learning.current_weights && Object.keys(learning.current_weights).length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ borderColor: '#1e293b', my: 0.5 }} />
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Current Weights</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {Object.entries(learning.current_weights).map(([k, v]) => (
                        <Chip key={k} label={`${k}: ${(v as number).toFixed(3)}`} size="small" sx={{ bgcolor: '#1e293b', color: '#94a3b8', fontSize: 10 }} />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* ── Harness Check ────────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#020617', border: '1px solid #1e293b', fontFamily: 'monospace' }}>
            <Typography variant="caption" sx={{ color: '#38bdf8', fontWeight: 700, letterSpacing: 1, display: 'block', mb: 1 }}>
              ═══ HARNESS CHECK ═══
            </Typography>
            <Box component="pre" sx={{ m: 0, color: '#94a3b8', fontSize: 12, lineHeight: 1.8 }}>
{`Current Scope:          MES / MNQ (micro-live, 1 contract)
Depth Score:            ${
  kazeOnline ? (hitRateNum !== null ? (hitRateNum >= 0.85 ? '≥0.85 (controlled-live eligible)' : hitRateNum >= 0.70 ? '≥0.70 (micro-live)' : '<0.70 (simulation only)') : 'computing…') : 'N/A (kaze offline)'
}
Drift Status:           ${gate4ok ? 'stable' : 'insufficient data'}
Risk State:             ${riskPosture ?? 'UNKNOWN'}
Expansion Eligibility:  ${gate1ok && gate2ok && gate4ok && hitRateNum !== null && hitRateNum >= 0.95 ? 'ELIGIBLE' : 'DENIED'}
Recommended Mode:       ${harnessMode}
Stream Quality:         ${kazeOnline ? (gate3ok ? 'green' : 'amber') : 'RED — kaze offline'}
Kaze Regime:            ${macroRegime} (${(macroConf * 100).toFixed(0)}% conf) | posture=${riskPosture ?? 'N/A'} | liquidity=${liquidity}`}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Shadow Tab ─────────────────────────────────────────────────────────────

function ShadowTab({ shadow, onReset }: { shadow: ShadowStats | null; onReset: () => void }) {
  if (!shadow) return <Typography sx={{ color: '#9ca3af' }}>No shadow data yet.</Typography>;
  const total = shadow.total || 1;
  const pct = (n: number) => ((n / total) * 100).toFixed(1) + '%';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ color: '#fff' }}>
          {shadow.total} shadow decisions evaluated
        </Typography>
        <Button onClick={onReset} size="small" variant="outlined" color="warning" startIcon={<RestartAlt />}>
          Reset stats
        </Button>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <KpiCard label="Agree" value={pct(shadow.divergences.none)} sub={`${shadow.divergences.none} samples`} accent="#10b981" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label="v2 abstains (live entered)" value={pct(shadow.divergences.flip_abstain)} sub={`${shadow.divergences.flip_abstain}`} accent="#f59e0b" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label="v2 enters (live abstained)" value={pct(shadow.divergences.flip_enter)} sub={`${shadow.divergences.flip_enter}`} accent="#f59e0b" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label="Size delta" value={pct(shadow.divergences.size_delta)} sub={`mean ${shadow.meanSizeDeltaContracts.toFixed(2)} ct`} accent="#60a5fa" />
        </Grid>
      </Grid>

      <Paper sx={{ bgcolor: '#111827', border: '1px solid #1f2937' }}>
        <Typography variant="subtitle2" sx={{ p: 2, color: '#9ca3af' }}>
          Last {Math.min(20, shadow.lastDecisions.length)} decisions
        </Typography>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Time', 'Signal', 'Live', 'v2', 'Δ', 'v2 Action', 'Divergence', 'Rationale'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#1f2937', color: '#9ca3af', fontSize: 11 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...shadow.lastDecisions].reverse().slice(0, 20).map((d) => (
                <TableRow key={d.signalId + d.ts} sx={{ '& td': { color: '#d1d5db', fontSize: 12 } }}>
                  <TableCell>{d.ts.slice(11, 19)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{d.signalId.slice(0, 10)}</TableCell>
                  <TableCell>{d.liveContracts}</TableCell>
                  <TableCell>{d.v2Contracts}</TableCell>
                  <TableCell sx={{ color: d.sizeDeltaContracts === 0 ? '#6b7280' : '#facc15' }}>
                    {d.sizeDeltaContracts > 0 ? '+' : ''}{d.sizeDeltaContracts}
                  </TableCell>
                  <TableCell>{d.v2Action}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={d.divergence}
                      color={d.divergence === 'none' ? 'default' : 'warning'}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#9ca3af', fontSize: 11 }}>{d.rationale.join(' · ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

// ─── Triple Gate Tab ────────────────────────────────────────────────────────

function TripleGateTab({ strategies }: { strategies: StrategiesResponse | null }) {
  if (!strategies) return <Typography sx={{ color: '#9ca3af' }}>Loading strategies…</Typography>;
  const PF_WF_FLOOR = 1.5;
  const PF_MC_FLOOR = 1.2;
  const VAR_CEIL = 0.04;

  const checkPillar = (val: number | undefined, floor: number, ceil = false) => {
    if (val === undefined || val === null) return { ok: false, label: '—' };
    if (ceil) return { ok: val <= floor, label: val.toFixed(3) };
    return { ok: val >= floor, label: val.toFixed(3) };
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2, bgcolor: '#1e3a8a', color: '#dbeafe' }}>
        Strategies must pass all three pillars: walk-forward PF ≥ {PF_WF_FLOOR}, Monte-Carlo p5 PF ≥ {PF_MC_FLOOR}, OOS variance ≤ {VAR_CEIL}.
        Missing data = automatic rejection.
      </Alert>
      <TableContainer component={Paper} sx={{ bgcolor: '#111827' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Strategy', 'Class', 'PF (WF)', 'PF (MC p5)', 'OOS Var', 'Registered'].map((h) => (
                <TableCell key={h} sx={{ bgcolor: '#1f2937', color: '#9ca3af', fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {strategies.strategies.map((s) => {
              const wf = checkPillar(s.walkForwardProfitFactor, PF_WF_FLOOR);
              const mc = checkPillar(s.monteCarloP5ProfitFactor, PF_MC_FLOOR);
              const va = checkPillar(s.outOfSampleReturnVariance, VAR_CEIL, true);
              const registered = strategies.registered.includes(s.id);
              return (
                <TableRow key={s.id} sx={{ '& td': { color: '#d1d5db', fontSize: 12 } }}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.id}</TableCell>
                  <TableCell>{s.classification ?? '—'}</TableCell>
                  <PillarCell ok={wf.ok} label={wf.label} />
                  <PillarCell ok={mc.ok} label={mc.label} />
                  <PillarCell ok={va.ok} label={va.label} />
                  <TableCell>
                    {registered ? <CheckCircle sx={{ color: '#10b981', fontSize: 18 }} /> : <Cancel sx={{ color: '#ef4444', fontSize: 18 }} />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PillarCell({ ok, label }: { ok: boolean; label: string }) {
  return (
    <TableCell>
      <Box display="flex" alignItems="center" gap={0.5}>
        {ok ? <CheckCircle sx={{ fontSize: 14, color: '#10b981' }} /> : <Warning sx={{ fontSize: 14, color: '#f59e0b' }} />}
        <span>{label}</span>
      </Box>
    </TableCell>
  );
}

// ─── Regime Tab ─────────────────────────────────────────────────────────────

function RegimeTab({ snapshot }: { snapshot: EngineSnapshot | null }) {
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2, bgcolor: '#1e3a8a', color: '#dbeafe' }}>
        Regime-conditioned veto: strategies with negative expectancy in the current market regime are abstained
        (lookback 30d, min 5 trades). Cold-start safe — defers to other gates when no history exists.
      </Alert>
      <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937' }}>
        <Typography variant="subtitle2" sx={{ color: '#9ca3af', mb: 1 }}>
          Current regime classifiers active per asset
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Regime payoff history populates as live trades flow through. Check `tomi_regime_states` and `tomi_trades`
          tables for raw data. Veto decisions are emitted as <code>REGIME_VETO</code> audit events.
        </Typography>
      </Paper>
    </Box>
  );
}

// ─── Meta Tab ───────────────────────────────────────────────────────────────

function MetaTab({ meta }: { meta: MetaSuggestion[] }) {
  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Suggestions only — never auto-applied. Min 50 samples required. Hard-clamped:
        depth_min ∈ [0.30, 0.75], kelly_lambda ∈ [0.05, 0.50], kelly_fraction_cap ∈ [0.005, 0.05].
      </Alert>
      {meta.length === 0 ? (
        <Paper sx={{ p: 4, bgcolor: '#111827', textAlign: 'center', border: '1px solid #1f2937' }}>
          <Typography variant="body1" sx={{ color: '#6b7280' }}>
            No suggestions yet. Need ≥ 50 shadow samples + flip-rate asymmetry &gt; 20% or |size delta| &gt; 0.5 ct.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {meta.map((s, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #facc15' }}>
                <Typography variant="subtitle2" sx={{ color: '#facc15', fontFamily: 'monospace' }}>
                  {s.parameter}
                </Typography>
                <Typography variant="h5" sx={{ color: '#fff', my: 1 }}>
                  {s.currentValue.toFixed(3)} → {s.suggestedValue.toFixed(3)}
                  <Chip
                    label={`${s.delta >= 0 ? '+' : ''}${s.delta.toFixed(3)}`}
                    size="small"
                    sx={{ ml: 1, bgcolor: s.delta >= 0 ? '#065f46' : '#7f1d1d', color: '#fff' }}
                  />
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>{s.rationale}</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  n={s.sampleSize} · {new Date(s.emittedAt).toLocaleString()}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ─── Trades Tab ─────────────────────────────────────────────────────────────

function TradesTab({ journal }: { journal: any[] }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color: '#9ca3af', mb: 2 }}>
        Live trade journal · dual-written to <code>tomi_trades</code> postgres on each fill.
      </Typography>
      {journal.length === 0 ? (
        <Paper sx={{ p: 4, bgcolor: '#111827', textAlign: 'center', border: '1px solid #1f2937' }}>
          <Typography sx={{ color: '#6b7280' }}>No trades in current journal. Engine may be idle or session fresh.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: '#111827', maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Time', 'Symbol', 'Side', 'Size', 'Entry', 'Exit', 'PnL', 'Outcome'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#1f2937', color: '#9ca3af', fontSize: 11 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {journal.slice(-50).reverse().map((t, i) => (
                <TableRow key={i} sx={{ '& td': { color: '#d1d5db', fontSize: 12 } }}>
                  <TableCell>{(t.timestamp ?? t.openedAt ?? '').slice(11, 19)}</TableCell>
                  <TableCell>{t.symbol ?? t.contractId}</TableCell>
                  <TableCell>{t.side ?? t.direction}</TableCell>
                  <TableCell>{t.contracts ?? t.size}</TableCell>
                  <TableCell>{t.entryPrice}</TableCell>
                  <TableCell>{t.exitPrice ?? '—'}</TableCell>
                  <TableCell sx={{ color: (t.realizedPnl ?? 0) >= 0 ? '#10b981' : '#ef4444' }}>
                    {t.realizedPnl != null ? `$${Number(t.realizedPnl).toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell>{t.outcome ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Controls Tab ───────────────────────────────────────────────────────────

function ControlsTab({
  snapshot,
  onAction,
}: {
  snapshot: EngineSnapshot | null;
  onAction: (path: string, label: string, confirm?: boolean) => void;
}) {
  const running = snapshot?.status === 'RUNNING';
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity={snapshot?.dryRun ? 'warning' : 'success'}>
          Engine is in <strong>{snapshot?.dryRun ? 'DRY-RUN' : 'LIVE'}</strong> mode.
          {snapshot?.dryRun
            ? ' No real orders will be submitted.'
            : ' Real orders WILL be submitted to ProjectX.'}
        </Alert>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937' }}>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>Engine Lifecycle</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Button variant="contained" color="primary" startIcon={<RestartAlt />} onClick={() => onAction('/initialize', 'Initialize engine')}>
              Initialize
            </Button>
            <Button variant="contained" color="success" startIcon={<PlayArrow />} disabled={running} onClick={() => onAction('/start', 'Start engine')}>
              Start
            </Button>
            <Button variant="contained" color="error" startIcon={<Stop />} disabled={!running} onClick={() => onAction('/stop', 'Stop engine', true)}>
              Stop
            </Button>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: '#111827', border: '1px solid #1f2937' }}>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>Risk Maintenance</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Button variant="outlined" color="warning" onClick={() => onAction('/clear-lockout', 'Clear fill-state lockout', true)}>
              Clear fill-state lockout
            </Button>
            <Button variant="outlined" color="warning" onClick={() => onAction('/reset-daily', 'Reset daily counters', true)}>
              Reset daily counters
            </Button>
            <Button variant="outlined" color="info" onClick={() => onAction('/v2/shadow/reset', 'Reset shadow stats', true)}>
              Reset shadow v2 stats
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

// ─── KPI Card ───────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <Paper
      sx={{
        p: 2,
        bgcolor: '#111827',
        border: '1px solid #1f2937',
        borderLeft: `3px solid ${accent ?? '#374151'}`,
        height: '100%',
      }}
    >
      <Typography variant="caption" sx={{ color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mt: 0.5 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}
